// Animated list primitive — adapted from magicui
// (https://magicui.design/docs/components/animated-list).
// `motion/react` swapped for `framer-motion` v12 to match our deps.
//
// `AnimatedList` reveals children one by one on a fixed `delay` cadence
// (newest on top, notification-style). `AnimatedListItem` is the unit
// wrapper — exported separately so callers can stagger items manually
// when they need top-to-bottom reveal order (e.g. a form where Q1 should
// land before Q2).

"use client";

import React, {
  useEffect,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import {
  AnimatePresence,
  motion,
  type MotionProps,
} from "framer-motion";

import { cn } from "@/lib/utils";

export function AnimatedListItem({
  children,
}: {
  children: React.ReactNode;
}) {
  const animations: MotionProps = {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1, originY: 0 },
    exit: { scale: 0, opacity: 0 },
    transition: { type: "spring", stiffness: 350, damping: 40 },
  };

  return (
    <motion.div {...animations} layout className="mx-auto w-full">
      {children}
    </motion.div>
  );
}

export interface AnimatedListProps extends ComponentPropsWithoutRef<"div"> {
  children: React.ReactNode;
  delay?: number;
}

export const AnimatedList = React.memo(
  ({
    children,
    className,
    delay = 1000,
    ...props
  }: AnimatedListProps) => {
    const [index, setIndex] = useState(0);
    const childrenArray = useMemo(
      () => React.Children.toArray(children),
      [children]
    );

    useEffect(() => {
      let timeout: ReturnType<typeof setTimeout> | null = null;

      if (index < childrenArray.length - 1) {
        timeout = setTimeout(() => {
          setIndex(
            (prevIndex) => (prevIndex + 1) % childrenArray.length
          );
        }, delay);
      }

      return () => {
        if (timeout !== null) {
          clearTimeout(timeout);
        }
      };
    }, [index, delay, childrenArray.length]);

    const itemsToShow = useMemo(() => {
      const result = childrenArray.slice(0, index + 1).reverse();
      return result;
    }, [index, childrenArray]);

    return (
      <div
        className={cn(`flex flex-col items-center gap-4`, className)}
        {...props}
      >
        <AnimatePresence>
          {itemsToShow.map((item) => (
            <AnimatedListItem key={(item as React.ReactElement).key}>
              {item}
            </AnimatedListItem>
          ))}
        </AnimatePresence>
      </div>
    );
  }
);

AnimatedList.displayName = "AnimatedList";

// Top-down stagger variant for ordered reveals (questions, options) where
// "newest on top" doesn't make sense. Each child gets a base delay + a
// per-index increment. Uses the same spring physics as the magicui
// AnimatedListItem (stiffness 350, damping 40) plus a noticeable initial
// scale + slide so the animation reads on first mount and on refresh.
export function StaggeredReveal({
  children,
  baseDelayMs = 0,
  stepMs = 120,
  className,
}: {
  children: React.ReactNode;
  baseDelayMs?: number;
  stepMs?: number;
  className?: string;
}) {
  const items = React.Children.toArray(children);
  return (
    <div className={className}>
      {items.map((child, i) => (
        <motion.div
          key={(child as React.ReactElement).key ?? i}
          initial={{ scale: 0.7, opacity: 0, y: 14 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 350,
            damping: 40,
            delay: (baseDelayMs + i * stepMs) / 1000,
          }}
          style={{ originY: 0 }}
        >
          {child}
        </motion.div>
      ))}
    </div>
  );
}
